/**
 * The person modal's role vocabulary.
 *
 * Role types exist in two spellings: the backend stores English (resident, staff,
 * visitor) while the UI offers Portuguese (morador, funcionario, visitante), and BOTH
 * come back from the API depending on how a role was created. Every rule here therefore
 * answers for either spelling — getting that wrong shows raw English in the UI, or asks
 * for a unit on a role that does not take one.
 */

export interface RoleTypeOption {
  value: string;
  label: string;
}

/**
 * Roles whose access is resolved through a UNIT.
 *
 * Excluded on purpose: porteiro and sindico, which are condomínio-wide and grant access
 * without a unit.
 */
const UNIT_BASED_ROLES = ['morador', 'resident'];

/**
 * Roles whose access is resolved through a DEPARTMENT.
 *
 * Staff access comes from department_profile_assignments joined on
 * person_roles.role_metadata->>'department_id' — the same shape as units, for empresa
 * customers. `funcionario` is what the UI offers; the backend stores it as `staff`.
 */
const DEPARTMENT_BASED_ROLES = ['funcionario', 'staff'];

const normalize = (roleType?: string | null): string => (roleType || '').trim().toLowerCase();

export const isUnitBasedRole = (roleType?: string | null): boolean =>
  UNIT_BASED_ROLES.includes(normalize(roleType));

export const isDepartmentBasedRole = (roleType?: string | null): boolean =>
  DEPARTMENT_BASED_ROLES.includes(normalize(roleType));

/**
 * The bucket CreatePersonRole drops a staff role into when no department is given. It is a
 * placeholder, not a department: nothing assigns profiles to it, so anyone filed under it
 * has no access. Treated as "no department" everywhere rather than as a real choice.
 */
export const FALLBACK_DEPARTMENT_NAME = 'Sem Departamento';

export const isFallbackDepartment = (name?: string | null): boolean =>
  normalize(name) === FALLBACK_DEPARTMENT_NAME.toLowerCase();

/** Human label for a role type, in either spelling. */
export function roleTypeLabel(roleType: string): string {
  const roleTypeMap: { [key: string]: string } = {
    // Portuguese keys
    morador: 'Morador',
    visitante: 'Visitante',
    prestador: 'Prestador de Serviço',
    funcionario: 'Funcionário',
    staff: 'Funcionário', // Backend may return 'staff' for funcionario
    condo_employee: 'Funcionário', // Condo-type customers use this role instead of funcionario
    porteiro: 'Porteiro',
    sindico: 'Síndico',
    // English keys (backend may return these)
    resident: 'Morador',
    visitor: 'Visitante',
    service_provider: 'Prestador de Serviço',
    delivery: 'Entregador',
  };
  return roleTypeMap[roleType] || roleType;
}

/**
 * Role types a given customer may assign. A condomínio has residents and condomínio-wide
 * staff; an empresa has employees resolved through departments. When the customer type is
 * unknown, offer only the roles that need neither a unit nor a department, so nothing can
 * be created in a state that grants no access.
 */
export function availableRoleTypesFor(customerType?: string | null): RoleTypeOption[] {
  if (customerType === 'condominio') {
    return [
      { value: 'morador', label: 'Morador' },
      { value: 'visitante', label: 'Visitante' },
      { value: 'prestador', label: 'Prestador de Serviço' },
      { value: 'porteiro', label: 'Porteiro' },
      { value: 'sindico', label: 'Síndico' },
    ];
  }

  if (customerType === 'empresa') {
    return [
      { value: 'funcionario', label: 'Funcionário' },
      { value: 'visitante', label: 'Visitante' },
      { value: 'prestador', label: 'Prestador de Serviço' },
    ];
  }

  return [
    { value: 'visitante', label: 'Visitante' },
    { value: 'prestador', label: 'Prestador de Serviço' },
  ];
}
